export type QuestionarioSourceId = "feedback" | "carreira" | "procalouro" | "saude";

export type QuestionariosSnapshot = Record<string, unknown> & {
  data_geracao?: unknown;
};

export type QuestionarioRecord = Record<string, unknown>;

export type QuestionarioSourceDefinition = {
  id: QuestionarioSourceId;
  label: string;
  shortLabel: string;
  description: string;
  collection: string;
  aliases: string[];
};

export type QuestionarioSourceState = QuestionarioSourceDefinition & {
  published: boolean;
  records: QuestionarioRecord[];
};

export type QuestionariosInspection = {
  generatedAt: string | null;
  sources: QuestionarioSourceState[];
  publishedSourceCount: number;
  totalResponses: number;
  uniqueRespondents: number;
};

export type DistributionItem = {
  label: string;
  value: number;
  percent: number;
};

export type Distribution = {
  title: string;
  subtitle: string;
  items: DistributionItem[];
};

export type SourceMetric = {
  label: string;
  value: string;
  detail: string;
  calculation: string;
  tone: "blue" | "green" | "gold" | "purple";
};

export type QuestionarioSourceAnalytics = {
  metrics: SourceMetric[];
  distributions: Distribution[];
};

export const QUESTIONARIO_SOURCES: QuestionarioSourceDefinition[] = [
  {
    id: "feedback",
    label: "Feedback dos ingressantes",
    shortLabel: "Feedback",
    description: "Satisfação com acesso, informações e solicitações de contato.",
    collection: "respostas_ead_lti",
    aliases: ["respostas_ead_lti", "feedback_ingressantes", "feedback"]
  },
  {
    id: "carreira",
    label: "Questionário de carreira",
    shortLabel: "Carreira",
    description: "Empregabilidade, áreas de interesse, objetivos e competências.",
    collection: "respostas_carreira_completo",
    aliases: ["respostas_carreira_completo", "questionario_carreira", "carreira"]
  },
  {
    id: "procalouro",
    label: "Pro Calouro",
    shortLabel: "Pro Calouro",
    description: "Perfil de ingresso, bolsas, formação anterior e contribuições esperadas.",
    collection: "respostas_procalouro",
    aliases: ["respostas_procalouro", "pro_calouro", "procalouro"]
  },
  {
    id: "saude",
    label: "Mack Pela Vida",
    shortLabel: "Mack Pela Vida",
    description: "Bem-estar, qualidade do sono, DASS-21 e qualidade de vida.",
    collection: "respostas_saude_bem_estar",
    aliases: ["respostas_saude_bem_estar", "mack_pela_vida", "saude_bem_estar"]
  }
];

const NESTED_CONTAINERS = ["questionarios", "questionnaires", "surveys"];

export function inspectQuestionariosSnapshot(snapshot: unknown): QuestionariosInspection {
  const root = asRecord(snapshot) || {};
  const containers = [root, ...NESTED_CONTAINERS.map((key) => asRecord(root[key])).filter(isRecord)];
  const sources = QUESTIONARIO_SOURCES.map((definition) => {
    const result = findPublishedCollection(containers, definition.aliases);
    return {
      ...definition,
      published: result.published,
      records: result.records
    };
  });
  const allRecords = sources.flatMap((source) => source.records);

  return {
    generatedAt: stringValue(root.data_geracao) || stringValue(root.generatedAt) || null,
    sources,
    publishedSourceCount: sources.filter((source) => source.published).length,
    totalResponses: allRecords.length,
    uniqueRespondents: uniqueRespondentCount(allRecords)
  };
}

export function buildSourceAnalytics(source: QuestionarioSourceState): QuestionarioSourceAnalytics {
  if (source.id === "feedback") return feedbackAnalytics(source.records);
  if (source.id === "carreira") return carreiraAnalytics(source.records);
  if (source.id === "procalouro") return proCalouroAnalytics(source.records);
  return saudeAnalytics(source.records);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value);
}

export function formatPercent(value: number): string {
  return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

export function formatSnapshotDate(value: string | null): string {
  if (!value) return "Data não informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

function feedbackAnalytics(records: QuestionarioRecord[]): QuestionarioSourceAnalytics {
  const contact = records.map((record) => yesNo(valueByKeys(record, ["q3", "quer_contato", "contato"]))).filter(Boolean) as string[];
  const contactRequested = contact.filter((value) => value === "Sim").length;
  const accessScores = numericValues(records, ["q1", "q1_acesso", "acesso_materiais"], 1, 5);
  const informationScores = numericValues(records, ["q2", "q2_informacoes", "informacoes_precisas"], 1, 5);
  const contactMethods = records.flatMap((record) => splitMultiValue(valueByKeys(record, ["q4", "tipos_contato", "meio_contato"])))
    .map(normalizeContactMethod)
    .filter(Boolean) as string[];

  return {
    metrics: [
      totalMetric(records.length),
      {
        label: "Solicitaram contato",
        value: countAndPercent(contactRequested, records.length),
        detail: `${formatNumber(contactRequested)} solicitações`,
        calculation: "Respostas marcadas como Sim na pergunta de solicitação de contato, divididas pelo total de respostas publicadas.",
        tone: "green"
      },
      averageMetric("Nota média de acesso", accessScores, "Média das respostas válidas de acesso aos materiais, em escala de 1 a 5."),
      averageMetric("Nota média de informações", informationScores, "Média das respostas válidas sobre clareza das informações, em escala de 1 a 5.", "purple")
    ],
    distributions: [
      distribution("Solicitação de contato", "Quantidade e participação entre as respostas com informação válida", contact),
      distribution("Meios de contato preferidos", "Quantidade e participação entre todas as preferências informadas", contactMethods)
    ]
  };
}

function carreiraAnalytics(records: QuestionarioRecord[]): QuestionarioSourceAnalytics {
  const workStatuses = records.map(workStatus).filter(Boolean) as string[];
  const employed = workStatuses.filter((value) => value === "Trabalhando").length;
  const sectors = records.map((record) => stringValue(valueByKeys(record, ["setor_interesse", "area_atuacao"]))).filter(isMeaningful);
  const objectives = records.map((record) => stringValue(valueByKeys(record, ["objetivo_curso", "objetivo", "futuro_cenario", "expectativa_curso"]))).filter(isMeaningful);
  const topSector = topLabel(sectors);
  const topObjective = topLabel(objectives);

  return {
    metrics: [
      totalMetric(records.length),
      {
        label: "Em atividade profissional",
        value: countAndPercent(employed, workStatuses.length),
        detail: `${formatNumber(workStatuses.length)} respostas classificáveis`,
        calculation: "Respondentes classificados como empregados ou com regime de trabalho informado, divididos pelas respostas com situação classificável.",
        tone: "green"
      },
      topMetric("Principal setor de interesse", topSector, sectors.length, "Setor ou área de atuação mais frequente entre as respostas que informaram esse campo."),
      topMetric("Principal objetivo", topObjective, objectives.length, "Objetivo de carreira mais frequente entre as respostas que informaram esse campo.", "purple")
    ],
    distributions: [
      distribution("Situação de trabalho", "Quantidade e participação entre as respostas classificáveis", workStatuses),
      distribution("Setores de interesse", "Cinco setores mais citados, com quantidade e participação", sectors, 5)
    ]
  };
}

function proCalouroAnalytics(records: QuestionarioRecord[]): QuestionarioSourceAnalytics {
  const scholarship = records.map((record) => yesNo(valueByKeys(record, ["bolsista"]))).filter(Boolean) as string[];
  const scholarshipYes = scholarship.filter((value) => value === "Sim").length;
  const modalities = records.map((record) => stringValue(valueByKeys(record, ["bolsa_modalidade", "modalidade_bolsa"]))).filter(isMeaningful);
  const courses = records.map((record) => stringValue(valueByKeys(record, ["curso", "course_name"]))).filter(isMeaningful);
  const education = records.map((record) => stringValue(valueByKeys(record, ["ensino_medio"]))).filter(isMeaningful);

  return {
    metrics: [
      totalMetric(records.length),
      {
        label: "Bolsistas",
        value: countAndPercent(scholarshipYes, scholarship.length),
        detail: `${formatNumber(scholarship.length)} respostas classificáveis`,
        calculation: "Respostas afirmativas no campo Bolsista, divididas pelas respostas com esse campo preenchido.",
        tone: "green"
      },
      topMetric("Bolsa mais frequente", topLabel(modalities), modalities.length, "Modalidade de bolsa mais frequente entre as respostas que informaram uma modalidade."),
      topMetric("Curso mais frequente", topLabel(courses), courses.length, "Curso mais frequente entre as respostas que informaram o curso.", "purple")
    ],
    distributions: [
      distribution("Modalidade de bolsa", "Quantidade e participação entre as modalidades informadas", modalities, 5),
      distribution("Formação no ensino médio", "Quantidade e participação entre as respostas informadas", education, 5)
    ]
  };
}

function saudeAnalytics(records: QuestionarioRecord[]): QuestionarioSourceAnalytics {
  const withAnswers = records.filter((record) => asRecord(record.respostas) !== null).length;
  const sleepQuality = records.map((record) => {
    const answers = asRecord(record.respostas);
    return answers ? sleepQualityLabel(answers.sono_q15) : "";
  }).filter(Boolean);
  const courses = records.map((record) => stringValue(valueByKeys(record, ["user_course", "curso", "course_name"]))).filter(isMeaningful);
  const respondents = uniqueRespondentCount(records);

  return {
    metrics: [
      totalMetric(records.length),
      {
        label: "Respondentes únicos",
        value: formatNumber(respondents),
        detail: `${countAndPercent(respondents, records.length)} das respostas`,
        calculation: "Quantidade de identificadores únicos encontrados por RA ou e-mail nas respostas publicadas.",
        tone: "green"
      },
      {
        label: "Questionários preenchidos",
        value: countAndPercent(withAnswers, records.length),
        detail: `${formatNumber(withAnswers)} com bloco de respostas`,
        calculation: "Registros que possuem o bloco estruturado de respostas, divididos pelo total de registros publicados.",
        tone: "gold"
      },
      topMetric("Curso mais frequente", topLabel(courses), courses.length, "Curso mais frequente entre as respostas que informaram o curso.", "purple")
    ],
    distributions: [
      distribution("Qualidade geral do sono", "Quantidade e participação entre as respostas válidas da questão 15", sleepQuality),
      distribution("Cursos dos respondentes", "Cinco cursos mais frequentes, com quantidade e participação", courses, 5)
    ]
  };
}

function totalMetric(total: number): SourceMetric {
  return {
    label: "Total de respostas",
    value: formatNumber(total),
    detail: "Registros publicados no snapshot",
    calculation: "Contagem de registros da coleção deste questionário presentes no snapshot protegido.",
    tone: "blue"
  };
}

function averageMetric(label: string, values: number[], calculation: string, tone: SourceMetric["tone"] = "gold"): SourceMetric {
  const average = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
  return {
    label,
    value: values.length ? `${average.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} (${formatPercent((average / 5) * 100)})` : "Sem dados",
    detail: `${formatNumber(values.length)} respostas válidas`,
    calculation,
    tone
  };
}

function topMetric(label: string, top: DistributionItem | null, validCount: number, calculation: string, tone: SourceMetric["tone"] = "gold"): SourceMetric {
  return {
    label,
    value: top?.label || "Sem dados",
    detail: top ? `${formatNumber(top.value)} (${formatPercent(top.percent)}) de ${formatNumber(validCount)}` : "Nenhuma resposta válida",
    calculation,
    tone
  };
}

function distribution(title: string, subtitle: string, values: string[], limit = 8): Distribution {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) || 0) + 1);
  const total = values.length;
  const items = [...counts.entries()]
    .map(([label, value]) => ({ label, value, percent: total ? (value / total) * 100 : 0 }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label, "pt-BR"))
    .slice(0, limit);
  return { title, subtitle, items };
}

function topLabel(values: string[]): DistributionItem | null {
  return distribution("", "", values, 1).items[0] || null;
}

function findPublishedCollection(containers: QuestionarioRecord[], aliases: string[]): { published: boolean; records: QuestionarioRecord[] } {
  for (const container of containers) {
    for (const alias of aliases) {
      if (!Object.prototype.hasOwnProperty.call(container, alias)) continue;
      const value = container[alias];
      if (!Array.isArray(value)) return { published: true, records: [] };
      return { published: true, records: value.map(asRecord).filter(isRecord) };
    }
  }
  return { published: false, records: [] };
}

function uniqueRespondentCount(records: QuestionarioRecord[]): number {
  const values = records.map((record) => respondentKey(record)).filter(Boolean);
  return new Set(values).size;
}

function respondentKey(record: QuestionarioRecord): string {
  const ra = stringValue(valueByKeys(record, ["ra", "user_ra", "student_ra", "sis_user_id", "lti_user_id"]));
  if (ra) return `ra:${normalize(ra).replace(/\D/g, "") || normalize(ra)}`;
  const email = stringValue(valueByKeys(record, ["email", "user_email", "student_email"]));
  return email ? `email:${email.toLowerCase()}` : "";
}

function workStatus(record: QuestionarioRecord): string {
  const employed = normalize(stringValue(record.esta_empregado));
  const experience = normalize(stringValue(record.trabalho_experiencia));
  const regime = normalize(stringValue(record.regime_trabalho));
  if (employed === "sim" || experience === "simtrabalho" || (regime && regime !== "naotrabalho" && regime !== "nao trabalho")) return "Trabalhando";
  if (employed === "nao" || experience === "naotrabalho" || regime === "naotrabalho" || regime === "nao trabalho") return "Não trabalhando";
  return "";
}

function yesNo(value: unknown): string {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  const normalized = normalize(stringValue(value));
  if (!normalized) return "";
  if (normalized === "sim" || normalized === "yes" || normalized === "true" || normalized === "1") return "Sim";
  if (normalized === "nao" || normalized === "no" || normalized === "false" || normalized === "0") return "Não";
  return "";
}

function numericValues(records: QuestionarioRecord[], keys: string[], min: number, max: number): number[] {
  return records.map((record) => Number(valueByKeys(record, keys)))
    .filter((value) => Number.isFinite(value) && value >= min && value <= max);
}

function valueByKeys(record: QuestionarioRecord, keys: string[]): unknown {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null && record[key] !== "") return record[key];
  }
  return undefined;
}

function splitMultiValue(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(stringValue).filter(Boolean);
  const text = stringValue(value);
  return text ? text.split(/[,;|]/).map((item) => item.trim()).filter(Boolean) : [];
}

function normalizeContactMethod(value: string): string {
  const normalized = normalize(value);
  if (normalized.includes("whatsapp")) return "WhatsApp";
  if (normalized.includes("mail")) return "E-mail";
  if (normalized.includes("telefone") || normalized.includes("phone")) return "Telefone";
  return value.trim();
}

function sleepQualityLabel(value: unknown): string {
  const text = stringValue(value);
  const labels: Record<string, string> = {
    "0": "Muito boa",
    "1": "Boa",
    "2": "Ruim",
    "3": "Muito ruim"
  };
  return labels[text] || text;
}

function countAndPercent(value: number, total: number): string {
  return `${formatNumber(value)} (${formatPercent(total ? (value / total) * 100 : 0)})`;
}

function isMeaningful(value: string): boolean {
  const normalized = normalize(value);
  return Boolean(normalized && normalized !== "-" && normalized !== "nao informado" && normalized !== "nao informada");
}

function normalize(value: string): string {
  return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
}

function stringValue(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
}

function asRecord(value: unknown): QuestionarioRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value as QuestionarioRecord : null;
}

function isRecord(value: QuestionarioRecord | null): value is QuestionarioRecord {
  return value !== null;
}
